<template>
    <transition name="hic-fade">
        <div
            v-if="open"
            class="hic-overlay"
            :class="{ 'hic-in-dashboard': isInDashboard }"
        >
            <div
                class="hic-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hic-title"
                aria-describedby="hic-body"
            >
                <h2 id="hic-title" class="hic-title">Before you continue</h2>

                <p id="hic-body" class="hic-body">
                    By continuing, you agree that we may securely use your health information to
                    coordinate your treatment with licensed providers and pharmacies, in
                    accordance with our
                    <a
                        class="hic-link"
                        :href="privacyUrl"
                        target="_blank"
                        rel="noopener"
                    >Privacy Policy</a>.
                </p>

                <button
                    ref="primaryBtn"
                    type="button"
                    class="hic-primary-btn"
                    @click="onAgree"
                >
                    I Agree &amp; Continue
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { healthConsentBus, resolveHealthConsent } from '@/utils/healthConsentGate';

export default {
    name: 'HealthInfoConsentModal',
    data() {
        return {
            open: false,
        };
    },
    computed: {
        privacyUrl() {
            const base = process.env.VUE_APP_HOMEPAGE || 'https://prioritymeds.com';
            return `${base}/privacy`;
        },
        isInDashboard() {
            return this.$route && typeof this.$route.path === 'string'
                && this.$route.path.indexOf('/home') === 0;
        },
    },
    created() {
        healthConsentBus.$on('hic-open', this.handleOpen);
        healthConsentBus.$on('hic-close', this.handleClose);
    },
    beforeDestroy() {
        healthConsentBus.$off('hic-open', this.handleOpen);
        healthConsentBus.$off('hic-close', this.handleClose);
        this.unlockScroll();
    },
    methods: {
        handleOpen() {
            this.open = true;
            this.lockScroll();
            this.$nextTick(() => {
                if (this.$refs.primaryBtn) this.$refs.primaryBtn.focus();
            });
        },
        handleClose() {
            this.open = false;
            this.unlockScroll();
        },
        onAgree() {
            resolveHealthConsent(true);
        },
        lockScroll() {
            this._prevBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        },
        unlockScroll() {
            if (typeof this._prevBodyOverflow === 'string') {
                document.body.style.overflow = this._prevBodyOverflow;
                this._prevBodyOverflow = undefined;
            } else {
                document.body.style.overflow = '';
            }
        },
    },
};
</script>

<style scoped>
.hic-overlay {
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    padding: 20px;
    box-sizing: border-box;
    font-family: "Funnel Sans", sans-serif;
}

.hic-card {
    position: relative;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    width: 100%;
    max-width: 480px;
    padding: 32px 28px 24px;
    box-sizing: border-box;
    border: 1px solid #e5e7eb;
}

.hic-title {
    margin: 0 0 14px;
    font-family: "Funnel Display", "Funnel Sans", sans-serif;
    font-size: 22px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
}

.hic-body {
    margin: 0 0 24px;
    font-size: 15px;
    line-height: 1.55;
    color: #374151;
}

.hic-link {
    color: #2e9bf0;
    text-decoration: underline;
}

.hic-link:hover {
    color: #00a36c;
}

.hic-primary-btn {
    display: block;
    width: 100%;
    padding: 16px 16px 18px;
    border: none;
    border-radius: 10px;
    background: #2e9bf0;
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.15s ease;
}

.hic-primary-btn:hover {
    background: #00a36c;
}

.hic-primary-btn:focus,
.hic-primary-btn:focus-visible,
.hic-primary-btn:active {
    outline: none;
    box-shadow: none;
}

/* Dashboard dark mode — ONLY activates on /home/* with [data-theme="dark"] */
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-card {
    background: #181818;
    border-color: #2e2e2e;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-title {
    color: #f5f5f5;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-body {
    color: #d4d4d4;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link {
    color: #7ad1e3;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link:hover {
    color: #52b2cf;
}

.hic-fade-enter-active,
.hic-fade-leave-active {
    transition: opacity 0.2s ease;
}
.hic-fade-enter-active .hic-card,
.hic-fade-leave-active .hic-card {
    transition: transform 0.2s ease;
}
.hic-fade-enter,
.hic-fade-leave-to {
    opacity: 0;
}
.hic-fade-enter .hic-card,
.hic-fade-leave-to .hic-card {
    transform: translateY(12px);
}

/* Mobile: bottom sheet */
@media (max-width: 639px) {
    .hic-overlay {
        align-items: flex-end;
        padding: 0;
    }
    .hic-card {
        max-width: 100%;
        border-radius: 18px 18px 0 0;
        padding: 26px 22px 24px;
        padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    }
    .hic-title {
        font-size: 20px;
    }
    .hic-body {
        font-size: 14.5px;
    }
    .hic-fade-enter .hic-card,
    .hic-fade-leave-to .hic-card {
        transform: translateY(100%);
    }
}
</style>
